import React, { useState } from "react";
import moment from "moment";
import dayjs from "dayjs";
import { Controller } from "react-hook-form";
import { Trash2 } from "react-feather";
import { Box, Button, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ListManager } from "react-beautiful-dnd-grid";
import tenantStyles from "../../tenantlist.module.css";
import styles from "./addTenant.module.css";
import { LEASE_TERM_LENGTHS, ListElement, MoneyField, formatMoney } from "./fields";

const ErrorText = ({ error }) => (error ? <p className={tenantStyles.ErrorM}>{error.message}</p> : null);

const DateField = ({ control, name, label, disabled, onChange, maxDate }) => (
  <Controller
    name={name}
    control={control}
    defaultValue={null}
    render={(field) => (
      <DatePicker
        {...field}
        label={label}
        renderInput={(params) => (<TextField {...params} fullWidth />)}
        onChange={(e) => {
          field.onChange(e);
          if (onChange) onChange(e);
        }}
        maxDate={maxDate}
        disabled={disabled}
      />
    )}
  />
);

const AmountField = ({ control, name, label, disabled }) => (
  <Controller
    name={name}
    control={control}
    defaultValue=""
    render={(field) => (
      <MoneyField {...field} variant="outlined" label={label} value={field.value ? field.value : ""} disabled={disabled} fullWidth />
    )}
  />
);

const StepLease = ({
  control, errors, setValue, getValues, clearErrors, leaseType, selectedApartment, watched,
  uploadFiles, fileHandler, removeFile, leaseDocumentOptions, selectDocument, selectDocuments,
  selectDocumentIds, dropdownOpen, setDropdownOpen, sortedLeaseDocuments, reorderList,
}) => {
  const [showLedger, setShowLedger] = useState(false);

  const isNew = leaseType === "New";
  const isRenewal = leaseType === "Renewal";
  const isExisting = leaseType === "Existing";
  const disabled = !selectedApartment;

  const shiftDate = (date, term, direction) => {
    const [amount, unit] = LEASE_TERM_LENGTHS[term] || LEASE_TERM_LENGTHS["1 Year"];
    return direction > 0
      ? dayjs(date).add(amount, unit).subtract(1, "day")
      : dayjs(date).subtract(amount, unit).add(1, "day");
  };

  const ledgerRows = [{ date: watched.start_date ? dayjs(watched.start_date).format("MM/DD/YYYY") : "Lease Start Date", desc: "Security deposit (charge)", amount: formatMoney(watched.security_amount) }];
  if (watched.security_received) {
    ledgerRows.push({ date: watched.security_received_date ? dayjs(watched.security_received_date).format("MM/DD/YYYY") : "Received date", desc: "Security deposit (payment)", amount: formatMoney(watched.security_amount) });
  }

  const uploadArea = (<div className={styles.FullRow}>
    <div className={tenantStyles.FileUpDuBUDiv}>
      <label htmlFor="add-tenant-file-upload" className={tenantStyles.UploadLabel}>
        <span>
          <div className={tenantStyles.UpIcon}>
            <img src="/static/img/upload-floor.svg" alt="" />
          </div>
          <p>Upload Existing Lease*</p>
          <p className={tenantStyles.UploadText}>File size upto 10 MB <br /> PDF File only</p>
        </span>
      </label>
      <input id="add-tenant-file-upload" type="file" onChange={fileHandler} accept=".pdf" />
    </div>
    <ErrorText error={errors.document_files} />
    <div className={tenantStyles.UpIMGSec}>
      {uploadFiles?.map((item, index) => {
        return (<div className={tenantStyles.ImgFile} key={index}>
          <div className={tenantStyles.ImgFileUnder}>
            <p className={tenantStyles.FileName}>
              <img src="/static/img/pdf-file.png" alt="" />
              <span>{item?.name}</span>
            </p>
            <p className={tenantStyles.FileSize}>{moment().format("MM/DD/YYYY hh:mm A")}</p>
            <div className={tenantStyles.FileListRight}>
              <Button className={tenantStyles.FileListDelete} onClick={() => removeFile(index)} aria-label="Remove file"><Trash2 /></Button>
            </div>
          </div>
        </div>);
      })}
    </div>
  </div>);

  return (
    <div className={styles.StepBody}>
      <div className={styles.Grid2}>
        <div className="FormGroup">
          <FormControl fullWidth>
            <InputLabel id="add-tenant-lease-term-label" className={disabled ? `DisabledLabel` : ``}>Lease term*</InputLabel>
            <Controller
              name="lease_term"
              control={control}
              render={(field) => (
                <Select
                  {...field}
                  labelId="add-tenant-lease-term-label"
                  label="Lease term*"
                  onChange={(e) => {
                    field.onChange(e);
                    if (getValues("start_date")) {
                      setValue("end_date", shiftDate(getValues("start_date"), e.target.value, 1));
                    }
                  }}
                  disabled={disabled}
                >
                  {Object.keys(LEASE_TERM_LENGTHS).map((term) => (<MenuItem key={term} value={term}>{term}</MenuItem>))}
                </Select>
              )}
            />
          </FormControl>
          <ErrorText error={errors.lease_term} />
        </div>
        <div className="FormGroup">
          <AmountField control={control} name="rent" label={isRenewal ? "New Monthly Rent*" : "Monthly Rent*"} disabled={disabled} />
          <ErrorText error={errors.rent} />
        </div>
        <div className="FormGroup">
          <DateField
            control={control}
            name="start_date"
            label="Lease Start Date*"
            disabled={disabled}
            onChange={(e) => {
              setValue("end_date", shiftDate(e, getValues("lease_term"), 1));
              clearErrors("end_date");
            }}
          />
          {isExisting && !errors.start_date && (<p className={styles.Hint}>Can be a past date (lease signed earlier)</p>)}
          <ErrorText error={errors.start_date} />
        </div>
        <div className="FormGroup">
          <DateField
            control={control}
            name="end_date"
            label="Lease End Date*"
            disabled={disabled}
            onChange={(e) => {
              setValue("start_date", shiftDate(e, getValues("lease_term"), -1));
              clearErrors("start_date");
            }}
          />
          <ErrorText error={errors.end_date} />
        </div>
        {/* One shared field so the amount survives switching the lease type. */}
        <div className="FormGroup">
          <AmountField control={control} name="security_amount" label={isRenewal ? "Old Security Amount*" : "Security Amount*"} disabled={disabled} />
          <ErrorText error={errors.security_amount} />
        </div>

        {isNew && (<>
          <div className="FormGroup">
            <DateField control={control} name="move_in_date" label="Move in date*" disabled={disabled} />
            <ErrorText error={errors.move_in_date} />
          </div>
          <div className="FormGroup">
            <AmountField control={control} name="first_rent" label="First Month Rent*" disabled={disabled} />
            <p className={styles.Hint}>Prorated when move-in differs from start date</p>
            <ErrorText error={errors.first_rent} />
          </div>
        </>)}

        {isRenewal && (<>
          <div className="FormGroup">
            <AmountField control={control} name="new_security_amount" label="New Security Amount*" disabled={disabled} />
            <p className={styles.Hint}>Only the increase (new minus old) is added</p>
            <ErrorText error={errors.new_security_amount} />
          </div>
          <div className="FormGroup">
            <FormControl fullWidth>
              <InputLabel id="add-tenant-payment-received-label" className={disabled ? `DisabledLabel` : ``}>Payment received?*</InputLabel>
              <Controller
                name="payment_received"
                control={control}
                defaultValue=""
                render={(field) => (
                  <Select
                    {...field}
                    labelId="add-tenant-payment-received-label"
                    label="Payment received?*"
                    onChange={(e) => {
                      field.onChange(e);
                      if (e.target.value !== "Yes") {
                        setValue("payment_date", null);
                        clearErrors("payment_date");
                      }
                    }}
                    disabled={disabled}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <ErrorText error={errors.payment_received} />
          </div>
          <div className="FormGroup">
            <DateField control={control} name="payment_date" label="Payment Date*" disabled={disabled || watched.payment_received !== "Yes"} />
            <ErrorText error={errors.payment_date} />
          </div>
        </>)}

        {isExisting && (<>
          <div className="FormGroup">
            <DateField control={control} name="move_in_date" label="Move in date*" disabled={disabled} maxDate={dayjs()} />
            <ErrorText error={errors.move_in_date} />
          </div>
          <div className="FormGroup">
            <TextField label="Deposit charge date" value="Same as Lease Start Date" InputProps={{ readOnly: true }} fullWidth className={styles.ReadOnlyField} />
          </div>
          <div className={`FormGroup ${styles.SwitchRow}`}>
            <Controller
              name="security_received"
              control={control}
              defaultValue={false}
              render={(field) => (
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!field.value}
                  aria-labelledby="add-tenant-received-label"
                  className={`${styles.Switch} ${field.value ? styles.SwitchOn : ""}`}
                  onClick={() => {
                    field.onChange(!field.value);
                    if (field.value) {
                      setValue("security_received_date", null);
                      clearErrors("security_received_date");
                    }
                  }}
                >
                  <span />
                </button>
              )}
            />
            <span id="add-tenant-received-label" className={styles.SwitchLabel}>Security deposit already received</span>
          </div>
          {watched.security_received && (<div className="FormGroup">
            <DateField control={control} name="security_received_date" label="Received date*" maxDate={dayjs()} />
            <ErrorText error={errors.security_received_date} />
          </div>)}
        </>)}
      </div>

      {isExisting && (<>
        <button type="button" className={styles.LinkBU} onClick={() => setShowLedger((prev) => !prev)} aria-expanded={showLedger}>
          {showLedger ? "Hide ledger preview" : "Preview ledger"}
        </button>
        {showLedger && (<div className={styles.Ledger}>
          <p className={styles.LedgerTitle}>Added to the ledger when the lease is confirmed</p>
          {ledgerRows.map((row) => (<div className={styles.LedgerRow} key={row.desc}>
            <span>{row.date}</span><span>{row.desc}</span><span>{row.amount}</span>
          </div>))}
          <div className={styles.LedgerTotal}>
            <span>Opening balance</span>
            <span className={watched.security_received ? styles.Paid : styles.Due}>{watched.security_received ? "$0.00" : `Due ${formatMoney(watched.security_amount)}`}</span>
          </div>
          <p className={styles.Hint}>No first month's rent bill is created. Regular rent bills start from the next bill date.</p>
        </div>)}
        {uploadArea}
      </>)}

      {!isExisting && (<div className={styles.Grid2}>
        <div className="FormGroup">
          <FormControl fullWidth>
            <InputLabel id="add-tenant-upload-label" className={disabled ? `DisabledLabel` : ``}>Upload an existing lease?*</InputLabel>
            <Controller
              name="is_existing"
              control={control}
              defaultValue=""
              render={(field) => (
                <Select
                  {...field}
                  labelId="add-tenant-upload-label"
                  label="Upload an existing lease?*"
                  onChange={(e) => {
                    field.onChange(e);
                    clearErrors(["docume_ids", "document_files"]);
                  }}
                  disabled={disabled}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              )}
            />
          </FormControl>
          <ErrorText error={errors.is_existing} />
        </div>
      </div>)}

      {!isExisting && watched.is_existing === "No" && (<div className="FormGroup">
        <FormControl fullWidth>
          <InputLabel id="add-tenant-documents-label">Select property lease documents*</InputLabel>
          <Select
            labelId="add-tenant-documents-label"
            value={selectDocument}
            onChange={selectDocuments}
            onClick={(e) => {
              if ((typeof e.target?.parentElement?.classList?.contains("MenuListNew") !== "undefined" && e.target?.parentElement?.classList.contains("MenuListNew")) || (typeof e.target?.parentElement?.parentElement?.classList?.contains("MenuListNew") !== "undefined" && e.target?.parentElement?.parentElement?.classList.contains("MenuListNew"))) {
                setDropdownOpen(true);
              } else if (e.target.localName === "span" && e.target.parentElement.classList.contains("DropListNew")) {
                setDropdownOpen(false);
              } else if (e.target.localName === "div" && ((typeof e.target?.parentElement?.classList?.contains("DropListNew") !== "undefined" && e.target?.parentElement?.classList.contains("DropListNew")) || (typeof e.target?.parentElement?.parentElement?.classList?.contains("DropListNew") !== "undefined" && e.target?.parentElement?.parentElement?.classList.contains("DropListNew")))) {
                setDropdownOpen(false);
              } else {
                setDropdownOpen((prev) => !prev);
              }
            }}
            multiple
            input={<OutlinedInput label="Select property lease documents*" />}
            open={dropdownOpen}
            renderValue={() => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }} className={"DropListNew"}>
                <ListManager
                  className={"DropListNew"}
                  items={sortedLeaseDocuments}
                  direction="horizontal"
                  maxItems={40}
                  render={(item) => (<ListElement item={item} />)}
                  onDragEnd={reorderList}
                />
              </Box>
            )}
          >
            {leaseDocumentOptions.map((item) => (
              <MenuItem key={item.id} value={item} className={"MenuListNew"}>
                <Checkbox checked={selectDocumentIds.indexOf(item.id) > -1} />
                <ListItemText primary={item.title} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ErrorText error={errors.docume_ids} />
      </div>)}

      {!isExisting && watched.is_existing === "Yes" && uploadArea}
    </div>
  );
};

export default StepLease;
